import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import { usePractice } from '../contexts/PracticeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, XCircle, Clock, AlertTriangle, Users, Building2, Sparkles, Crown, Zap, Watch, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DENTAL_PULSE_LOGO = 'https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png';

const TIER_CONFIG = {
  trial: { 
    icon: Clock, 
    color: 'bg-slate-100 text-slate-700 border-slate-300',
    colorDark: 'bg-slate-700 text-slate-200 border-slate-600'
  },
  basic: { 
    icon: Building2, 
    color: 'bg-dp-100 text-dp-700 border-dp-300',
    colorDark: 'bg-dp-900/50 text-dp-200 border-dp-700'
  },
  professional: { 
    icon: Sparkles, 
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    colorDark: 'bg-purple-900/50 text-purple-200 border-purple-700'
  },
  enterprise: { 
    icon: Crown, 
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    colorDark: 'bg-amber-900/50 text-amber-200 border-amber-700'
  }
};

export default function LicensePage() {
  const { darkMode } = useTheme();
  const { practice } = usePractice();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [smartwatchSeats, setSmartwatchSeats] = useState(null);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const [purchaseBillingCycle, setPurchaseBillingCycle] = useState('monthly');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [licenseRes, tiersRes, seatsRes] = await Promise.all([
        axios.get(`${API}/license/info`),
        axios.get(`${API}/license/tiers`),
        axios.get(`${API}/license/smartwatch-seats`)
      ]);
      setLicenseInfo(licenseRes.data);
      setTiers(tiersRes.data.tiers);
      setSmartwatchSeats(seatsRes.data);
    } catch (error) {
      toast.error('Failed to fetch license information');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (tierName) => {
    try {
      const tier = tiers[tierName];
      const pricingRes = await axios.get(`${API}/payments/calculate-surcharge`, {
        params: { amount: tier.monthly_price }
      });
      const pricing = pricingRes.data;
      
      const confirmed = window.confirm(
        `Upgrade to ${tier.display_name}?\n\n` +
        `Base Price: $${pricing.base_amount}\n` +
        `Credit Card Processing Fee: $${pricing.surcharge_amount}\n` +
        `Total: $${pricing.total_amount}\n\n` +
        `Click OK to proceed to secure checkout.`
      );
      
      if (!confirmed) return;
      
      const checkoutRes = await axios.post(`${API}/payments/create-checkout`, {
        subscription_tier: tierName,
        billing_cycle: 'monthly'
      });
      
      if (checkoutRes.data.success) {
        window.location.href = checkoutRes.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handlePurchaseSmartwatchSeats = async () => {
    try {
      const pricePerSeat = purchaseBillingCycle === 'yearly' 
        ? smartwatchSeats.pricing.yearly_per_seat 
        : smartwatchSeats.pricing.monthly_per_seat;
      const baseAmount = purchaseQuantity * pricePerSeat;
      
      const pricingRes = await axios.get(`${API}/payments/calculate-surcharge`, {
        params: { amount: baseAmount }
      });
      const pricing = pricingRes.data;
      
      const cycleLabel = purchaseBillingCycle === 'yearly' ? 'year' : 'month';
      const savingsNote = purchaseBillingCycle === 'yearly' 
        ? `\n(16% savings vs monthly!)` 
        : '';
      
      const confirmed = window.confirm(
        `Purchase ${purchaseQuantity} Smartwatch Seat(s)?\n\n` +
        `Billing: ${purchaseBillingCycle === 'yearly' ? 'Yearly' : 'Monthly'}\n` +
        `Price per seat: $${pricePerSeat}/${cycleLabel}${savingsNote}\n` +
        `Base Total: $${pricing.base_amount}\n` +
        `Credit Card Processing Fee: $${pricing.surcharge_amount}\n` +
        `Total: $${pricing.total_amount}\n\n` +
        `Click OK to proceed to secure checkout.`
      );
      
      if (!confirmed) return;
      
      const checkoutRes = await axios.post(`${API}/license/smartwatch-seats/purchase`, {
        quantity: purchaseQuantity,
        billing_cycle: purchaseBillingCycle
      });
      
      if (checkoutRes.data.success) {
        window.location.href = checkoutRes.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate purchase');
    }
  };

  // Apply custom background from practice settings
  const bgStyle = darkMode ? {
    backgroundColor: '#0f172a',
    backgroundImage: practice?.background_image_dark ? `url(${practice.background_image_dark})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  } : {
    backgroundColor: practice?.background_color || '#f8fafc',
    backgroundImage: practice?.background_image ? `url(${practice.background_image})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'dark' : ''}`} style={bgStyle}>
        <div className="text-center">
          <Zap className="w-12 h-12 animate-pulse text-dp-600 mx-auto mb-4" />
          <p className={darkMode ? 'text-slate-300' : 'text-slate-600'}>Loading license information...</p>
        </div>
      </div>
    );
  }

  if (!licenseInfo) {
    return (
      <div className={`min-h-screen ${darkMode ? 'dark' : ''}`} style={bgStyle}>
        <Sidebar />
        <div className="pl-28 pr-8 pt-6">
          <Alert className="border-red-300 bg-red-50">
            <XCircle className="h-5 w-5 text-red-600" />
            <AlertDescription className="text-red-800 ml-2">
              Failed to load license information. Please try again.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const { license, verification, current_usage } = licenseInfo;
  const tierConfig = TIER_CONFIG[license.subscription_tier] || TIER_CONFIG.trial;
  const TierIcon = tierConfig.icon;
  const operatoryUsage = (current_usage.operatories / license.max_operatories) * 100;
  const userUsage = (current_usage.users / license.max_users) * 100;

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`} style={bgStyle}>
      <Sidebar />
      
      <div className="pl-28 pr-8 pt-6 pb-8">
        <div className={`glass-panel rounded-2xl p-6 mb-6 flex items-center justify-between ${darkMode ? 'bg-slate-800/50' : ''}`}>
          <div>
            <h1 className={`text-3xl font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Subscription & Licensing</h1>
            <p className={`mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage your DentalPulse subscription</p>
          </div>
          <div className="flex-shrink-0 ml-4">
            <img 
              src={DENTAL_PULSE_LOGO} 
              alt="DentalPulse"
              className="h-12 w-auto object-contain"
              data-testid="dentalpulse-logo"
            />
          </div>
        </div>

        {verification.requires_renewal && (
          <Alert className={`mb-6 ${darkMode ? 'border-amber-600 bg-amber-900/50' : 'border-amber-300 bg-amber-50'}`}>
            <AlertTriangle className={`h-5 w-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            <AlertDescription className={`font-medium ml-2 ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>
              {verification.message}
            </AlertDescription>
          </Alert>
        )}

        {!verification.is_valid && (
          <Alert className={`mb-6 ${darkMode ? 'border-red-600 bg-red-900/50' : 'border-red-300 bg-red-50'}`}>
            <XCircle className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
            <AlertDescription className={`font-medium ml-2 ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
              {verification.message}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card className={`border-0 lg:col-span-2 ${darkMode ? 'bg-slate-800/50' : 'glass-panel'}`} data-testid="current-plan-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl ${darkMode ? tierConfig.colorDark : tierConfig.color} flex items-center justify-center`}>
                    <TierIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className={`text-2xl ${darkMode ? 'text-slate-100' : ''}`}>
                      {tiers?.[license.subscription_tier]?.display_name || license.subscription_tier}
                    </CardTitle>
                    <CardDescription className={darkMode ? 'text-slate-400' : ''}>Current subscription plan</CardDescription>
                  </div>
                </div>
                
                <Badge variant={verification.is_valid ? "default" : "destructive"} className="text-sm">
                  {verification.is_valid ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                  {verification.status.toUpperCase()}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className={`text-sm mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>License Key</p>
                  <code className={`text-sm font-mono px-3 py-2 rounded ${darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100'}`}>{license.license_key}</code>
                </div>

                {license.subscription_end_date && (
                  <div>
                    <p className={`text-sm mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      {license.subscription_tier === 'trial' ? 'Trial Ends' : 'Renewal Date'}
                    </p>
                    <div className="flex items-center gap-2">
                      <Clock className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : ''}`}>
                        {new Date(license.subscription_end_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                        {verification.days_remaining !== null && (
                          <span className={`ml-2 ${verification.days_remaining <= 7 ? 'text-amber-500' : (darkMode ? 'text-slate-400' : 'text-slate-600')}`}>
                            ({verification.days_remaining} days remaining)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className={`border-0 ${darkMode ? 'bg-slate-800/50' : 'glass-panel'}`}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-slate-100' : ''}>Usage</CardTitle>
              <CardDescription className={darkMode ? 'text-slate-400' : ''}>Current vs limits</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`} />
                    <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : ''}`}>Operatories</span>
                  </div>
                  <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {current_usage.operatories} / {license.max_operatories}
                  </span>
                </div>
                <Progress value={operatoryUsage} className="h-2" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`} />
                    <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : ''}`}>Users</span>
                  </div>
                  <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {current_usage.users} / {license.max_users}
                  </span>
                </div>
                <Progress value={userUsage} className="h-2" />
              </div>

              {license.billing_cycle && (
                <div className={`pt-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Billing Cycle</p>
                  <p className={`text-lg font-semibold capitalize ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{license.billing_cycle}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Smartwatch Seats Panel */}
        {smartwatchSeats && (
          <div className={`rounded-2xl p-6 mb-6 ${darkMode ? 'bg-slate-800/50' : 'glass-panel'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${darkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                  <Watch className="w-5 h-5" />
                </div>
                <div>
                  <h2 className={`text-xl font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Smartwatch Seats</h2>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage smartwatch access for your team</p>
                </div>
              </div>
              
              <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="purchase-smartwatch-seats-btn"
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Purchase Seats
                  </Button>
                </DialogTrigger>
                <DialogContent className={darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                  <DialogHeader>
                    <DialogTitle className={darkMode ? 'text-slate-100' : ''}>Purchase Smartwatch Seats</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-6 mt-4">
                    {/* Quantity Selector */}
                    <div>
                      <Label className={darkMode ? 'text-slate-200' : ''}>Number of Seats</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setPurchaseQuantity(Math.max(1, purchaseQuantity - 1))}
                          disabled={purchaseQuantity <= 1}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={purchaseQuantity}
                          onChange={(e) => setPurchaseQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                          className={`w-20 text-center ${darkMode ? 'bg-slate-700 text-slate-100 border-slate-600' : ''}`}
                          data-testid="smartwatch-quantity-input"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setPurchaseQuantity(Math.min(100, purchaseQuantity + 1))}
                          disabled={purchaseQuantity >= 100}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Billing Cycle Selector */}
                    <div>
                      <Label className={darkMode ? 'text-slate-200' : ''}>Billing Cycle</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <button
                          onClick={() => setPurchaseBillingCycle('monthly')}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            purchaseBillingCycle === 'monthly'
                              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30'
                              : darkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200'
                          }`}
                          data-testid="billing-monthly-btn"
                        >
                          <div className={`font-semibold ${darkMode ? 'text-slate-100' : ''}`}>Monthly</div>
                          <div className={`text-lg font-bold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                            ${smartwatchSeats.pricing.monthly_per_seat}/seat/mo
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Auto-renews, cancel anytime
                          </div>
                        </button>
                        
                        <button
                          onClick={() => setPurchaseBillingCycle('yearly')}
                          className={`p-4 rounded-lg border-2 text-left transition-all relative ${
                            purchaseBillingCycle === 'yearly'
                              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30'
                              : darkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200'
                          }`}
                          data-testid="billing-yearly-btn"
                        >
                          <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                            Save {smartwatchSeats.pricing.yearly_savings_percent}%
                          </div>
                          <div className={`font-semibold ${darkMode ? 'text-slate-100' : ''}`}>Yearly</div>
                          <div className={`text-lg font-bold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                            ${smartwatchSeats.pricing.yearly_per_seat}/seat/yr
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Prepaid for full year, no refunds
                          </div>
                        </button>
                      </div>
                    </div>
                    
                    {/* Summary */}
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                      <div className="flex justify-between items-center">
                        <span className={darkMode ? 'text-slate-300' : 'text-slate-600'}>Total</span>
                        <span className={`text-2xl font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                          ${purchaseQuantity * (purchaseBillingCycle === 'yearly' 
                            ? smartwatchSeats.pricing.yearly_per_seat 
                            : smartwatchSeats.pricing.monthly_per_seat
                          )}
                          <span className={`text-sm font-normal ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            /{purchaseBillingCycle === 'yearly' ? 'year' : 'month'}
                          </span>
                        </span>
                      </div>
                      <p className={`text-xs mt-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        + credit card processing fee
                      </p>
                    </div>
                    
                    {/* Info Note - Show for both monthly and yearly */}
                    <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                      <p className="text-sm">
                        {purchaseBillingCycle === 'monthly' ? (
                          <>
                            <strong>Auto-Renewal:</strong> Monthly seats auto-renew each billing cycle. To reduce seats, unassign users before the next billing date. Unassigned seats will not renew. No refunds once purchased.
                          </>
                        ) : (
                          <>
                            <strong>Note:</strong> Yearly seats are prepaid for the full year. Seats must be assigned to users to be active. No refunds for unused or unassigned seats.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <DialogFooter className="mt-6">
                    <Button variant="outline" onClick={() => setPurchaseDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handlePurchaseSmartwatchSeats}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="confirm-purchase-btn"
                    >
                      Proceed to Checkout
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            
            {/* Seats Summary Grid */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-white'}`}>
                <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Purchased</div>
                <div className={`text-3xl font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`} data-testid="seats-purchased">
                  {smartwatchSeats.purchased}
                </div>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-white'}`}>
                <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Assigned</div>
                <div className={`text-3xl font-bold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} data-testid="seats-assigned">
                  {smartwatchSeats.assigned}
                </div>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-white'}`}>
                <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Available</div>
                <div className={`text-3xl font-bold ${smartwatchSeats.available > 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-slate-500' : 'text-slate-400')}`} data-testid="seats-available">
                  {smartwatchSeats.available}
                </div>
              </div>
            </div>
            
            {/* Assigned Users List */}
            {smartwatchSeats.assigned_users && smartwatchSeats.assigned_users.length > 0 && (
              <div className={`border-t pt-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Assigned Team Members
                </h3>
                <div className="space-y-2">
                  {smartwatchSeats.assigned_users.map((user) => (
                    <div 
                      key={user.user_id} 
                      className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        <Watch className={`w-4 h-4 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} />
                        <div>
                          <div className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                            {user.name || user.username}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {user.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} seat
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Info Box */}
            <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-slate-700/50 border border-slate-600' : 'bg-slate-50 border border-slate-200'}`}>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                <strong>How it works:</strong> Purchase smartwatch seats first, then assign access to team members in Admin Settings → Team Management. 
                Monthly seats auto-renew unless unassigned before the billing date. No refunds once purchased.
              </p>
            </div>
          </div>
        )}

        {license.subscription_tier !== 'enterprise' && tiers && (
          <div className={`rounded-2xl p-6 ${darkMode ? 'bg-slate-800/50' : 'glass-panel'}`}>
            <h2 className={`text-2xl font-heading font-bold mb-6 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Upgrade Your Plan</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(tiers).map(([tierName, tier]) => {
                if (tierName === license.subscription_tier || tierName === 'trial') return null;
                const config = TIER_CONFIG[tierName] || TIER_CONFIG.basic;
                const Icon = config.icon;
                
                return (
                  <Card key={tierName} className={`border-2 hover:shadow-lg transition-all ${darkMode ? config.colorDark : config.color}`}>
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className="w-8 h-8" />
                        <CardTitle className={`text-xl ${darkMode ? 'text-slate-100' : ''}`}>{tier.display_name}</CardTitle>
                      </div>
                      <div className={`text-3xl font-bold ${darkMode ? 'text-slate-100' : ''}`}>
                        ${tier.monthly_price}
                        <span className={`text-sm font-normal ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>/month</span>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        ${tier.annual_price}/year (Save ${(tier.monthly_price * 12 - tier.annual_price).toFixed(0)})
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 mb-4">
                        {tier.features?.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                            <span className={`text-sm ${darkMode ? 'text-slate-200' : ''}`}>{feature}</span>
                          </div>
                        ))}
                      </div>
                      <Button
                        onClick={() => handleUpgrade(tierName)}
                        data-testid={`upgrade-${tierName}-button`}
                        className="w-full bg-slate-800 hover:bg-slate-900 text-white"
                      >
                        Upgrade to {tier.display_name}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
